import { Button, DatePicker, Dropdown, Segmented, Space, Typography } from "antd";
import { DownOutlined, ThunderboltOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import trans from "../../../utils";
import {
  currentMonthKey,
  isComparableMonth,
  shiftMonthKey,
} from "./retail/retailCompare";

const { Text } = Typography;

const toMonthValue = (monthKey) => (monthKey ? dayjs(`${monthKey}-01`) : null);

/**
 * Retail period mode switch ("Single period" / "Compare months") plus, in
 * compare mode, the two month pickers and one-click presets. Months are
 * "YYYY-MM" keys inside the API's window (current month back twelve months).
 */
export default function RetailComparePicker({
  enabled,
  monthA,
  monthB,
  todayIso,
  onToggle,
  onChange,
}) {
  const thisMonth = currentMonthKey(todayIso);
  const presets = [
    {
      key: "this-vs-last",
      label: trans("This month vs last month"),
      monthA: thisMonth,
      monthB: shiftMonthKey(thisMonth, -1),
    },
    {
      key: "last-vs-before",
      label: trans("Last month vs the month before"),
      monthA: shiftMonthKey(thisMonth, -1),
      monthB: shiftMonthKey(thisMonth, -2),
    },
    {
      key: "this-vs-last-year",
      label: trans("This month vs same month last year"),
      monthA: thisMonth,
      monthB: shiftMonthKey(thisMonth, -12),
    },
  ].filter(
    (preset) =>
      isComparableMonth(preset.monthA, todayIso) &&
      isComparableMonth(preset.monthB, todayIso),
  );

  const isDisabledMonth = (current, excludedMonth) => {
    if (!current) return false;
    const key = current.format("YYYY-MM");
    return !isComparableMonth(key, todayIso) || key === excludedMonth;
  };

  return (
    <Space size={[8, 6]} wrap>
      <Segmented
        value={enabled ? "compare" : "single"}
        onChange={(value) => onToggle(value === "compare")}
        options={[
          { value: "single", label: trans("Single period") },
          { value: "compare", label: trans("Compare months") },
        ]}
        aria-label={trans("Retail period")}
      />
      {enabled ? (
        <>
          <Space size={4}>
            <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
              {trans("Month")}
            </Text>
            <DatePicker
              picker="month"
              allowClear={false}
              value={toMonthValue(monthA)}
              format="MMM YYYY"
              disabledDate={(current) => isDisabledMonth(current)}
              onChange={(value) =>
                value && onChange({ monthA: value.format("YYYY-MM") })
              }
              aria-label={trans("Selected month")}
              style={{ width: 118 }}
            />
          </Space>
          <Space size={4}>
            <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
              {trans("Compare with")}
            </Text>
            <DatePicker
              picker="month"
              allowClear={false}
              value={toMonthValue(monthB)}
              format="MMM YYYY"
              disabledDate={(current) => isDisabledMonth(current, monthA)}
              onChange={(value) =>
                value && onChange({ monthB: value.format("YYYY-MM") })
              }
              aria-label={trans("Comparison month")}
              style={{ width: 118 }}
            />
          </Space>
          {presets.length ? (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: presets.map((preset) => ({
                  key: preset.key,
                  label: preset.label,
                })),
                onClick: ({ key }) => {
                  const preset = presets.find((item) => item.key === key);
                  if (preset) {
                    onChange({ monthA: preset.monthA, monthB: preset.monthB });
                  }
                },
              }}
            >
              <Button icon={<ThunderboltOutlined />}>
                {trans("Quick picks")} <DownOutlined />
              </Button>
            </Dropdown>
          ) : null}
        </>
      ) : null}
    </Space>
  );
}
